import {makeObservable, observable} from "mobx";
import type {TransportLayer} from "../TransportLayer";
import type {DocumentModel} from "../models/DocumentModel";
import {isImage} from "../models/FileType";
import type {AttachDocumentRequest, CreateDocumentRequest, FullDocumentDto} from "../../generated/api/base";
import {FileType, XyiconFeature} from "../../generated/api/base";
import {ConfirmWindow} from "../../ui/modules/abstract/popups/ConfirmWindow";
import {FileUtils} from "../../utils/file/FileUtils";
import {XHRCancelToken, XHRLoader} from "../../utils/loader/XHRLoader";
import type {IModel} from "../models/Model";
import {ImageUtils} from "../../utils/image/ImageUtils";
import {DateFormatter} from "../../utils/format/DateFormatter";
import {ConfirmWindowV5} from "../../ui/5.0/popup/ConfirmWindowV5";
import {notify} from "../../utils/Notify";
import {NotificationType} from "../../ui/notification/Notification";
import type {Xyicon} from "../../data/models/Xyicon";
import type {Boundary} from "../../data/models/Boundary";
import {useAppStore} from "../../StateManager";

interface IDocumentUpload {
	count: number;
	id: string;
}

export class DocumentService {
	private readonly _transportLayer: TransportLayer;

	@observable
	public readonly uploadArray: IDocumentUpload[] = [];

	constructor(transportLayer: TransportLayer) {
		this._transportLayer = transportLayer;
		makeObservable(this);
	}

	public getFilePath(document: DocumentModel) {
		return this._transportLayer.getFullPathFromServer(`documents/files/${document.id}.${document.fileExtension}`);
	}

	public getThumbnailPath(document: DocumentModel) {
		if (isImage(document.fileExtension)) {
			// thumbnail is always a png
			return this._transportLayer.getFullPathFromServer(`documents/thumbnails/${document.id}.png`);
		} else if (document.fileType === FileType.PDF) {
			return "src/assets/images/common/pdf.svg";
		} else {
			return "src/assets/images/common/other.svg";
		}
	}

	public download(document: DocumentModel) {
		const fileUrl = this.getFilePath(document);
		const {fileName, fileExtension} = document;
		const fileNameWithoutExtension = fileName.substring(0, fileName.indexOf(`.${fileExtension}`)) || fileName;

		// in case of a user uploads a file without extension (in that way the name === extension)
		const extension = fileName === fileExtension ? "" : `.${fileExtension}`;

		FileUtils.downloadFileFromUrl(fileUrl, `${fileNameWithoutExtension}_${DateFormatter.timeStampForDownload()}${extension}`);
	}

	private readonly locateItemInSpaceEditor = (item: IModel) => {
		this._transportLayer.appState.actions.navigateToSpaceItem(item as Boundary | Xyicon, true);
	};

	private getPortfolioId(item: IModel) {
		if (item.ownFeature === XyiconFeature.Portfolio) {
			return item.id;
		}
		return item.portfolioId ?? this._transportLayer.appState.activePortfolioId;
	}

	public async createDocument(file: File, item: IModel) {
		const cancelToken = new XHRCancelToken();
		const isScopeWideSearchOpen = useAppStore.getState().isScopeWideSearchOpen; // dont want to show the locate icon when opened the portfoliowidesearch from the spaces
		const isLocate = this._transportLayer.appState.space && !isScopeWideSearchOpen;

		try {
			const notificationMessage = notify(this._transportLayer.appState.app.notificationContainer, {
				title: `Uploading a Photo to ${item.refId}`,
				type: NotificationType.Message,
				isMiniNotification: true,
				isIconVisible: false,
				progress: 0,
				description: "0%",
				lifeTime: Infinity,
				buttonLabel: "Cancel",
				onActionButtonClick: () => {
					cancelToken.cancel();
					notify(this._transportLayer.appState.app.notificationContainer, {
						title: `Failed to Upload to ${item.refId}`,
						type: NotificationType.Error,
						lifeTime: Infinity,
						isMiniNotification: true,
						isIconVisible: false,
						progress: 0,
						buttonLabel: "Try Again",
						onActionButtonClick: () => {
							this.createDocument(file, item);
						},
						onLocate: isLocate ? () => this.locateItemInSpaceEditor(item) : undefined,
					});
				},
			});

			const arrayBuffer = await FileUtils.readAsArrayBuffer(file);
			const sourceFile = Array.from(new Uint8Array(arrayBuffer));
			const thumbnail = await this.createThumbnailData(file);

			const {name, extension} = FileUtils.decomposeFileName(file.name);

			const data: CreateDocumentRequest = {
				sourceFile: sourceFile as any,
				thumbnail: thumbnail,
				fileName: file.name || "",
				fileExtension: extension,
				portfolioID: this.getPortfolioId(item),
			};

			const onUploadProgress = (progress: number) => {
				const roundedProgress = Math.round(progress * 100);

				if (notificationMessage.onProgress) {
					notificationMessage.onProgress(roundedProgress);
				}

				if (roundedProgress >= 100) {
					notificationMessage.onClose();
					notify(this._transportLayer.appState.app.notificationContainer, {
						title: `Photo Uploaded to ${item.refId}`,
						description: `${Math.round(progress * 100)}%`,
						type: NotificationType.Success,
						lifeTime: 5000,
						isMiniNotification: true,
						isIconVisible: false,
						progress: 0,
						buttonLabel: "",
						onLocate: isLocate ? () => this.locateItemInSpaceEditor(item) : undefined,
					});
				}
			};

			const feature = [XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog].includes(item.ownFeature)
				? XyiconFeature.OrganizationDocument
				: XyiconFeature.PortfolioDocument;
			const documents = await this._transportLayer.services.feature.create<DocumentModel>(data, feature, onUploadProgress, cancelToken);
			const document = documents[0];

			if (document) {
				const params: AttachDocumentRequest = {
					documentID: document.id,
					objectList: [
						{
							feature: item.ownFeature,
							objectIDList: [item.id],
						},
					],
				};

				const {result, error} = await this._transportLayer.requestForOrganization<FullDocumentDto>({
					url: "documents/attach",
					method: XHRLoader.METHOD_POST,
					params,
					cancelToken,
				});

				if (result) {
					document.applyData(result);
				}
			}
		} catch (error) {
			console.warn(error);
			notify(this._transportLayer.appState.app.notificationContainer, {
				title: `Failed to Upload to ${item.refId}`,
				type: NotificationType.Error,
				lifeTime: Infinity,
				isMiniNotification: true,
				isIconVisible: false,
				progress: 0,
				onLocate: isLocate ? () => this.locateItemInSpaceEditor(item) : undefined,
			});
		}
	}

	private async createThumbnailData(file: File) {
		const isImage = FileUtils.isImageFile(file);

		if (isImage) {
			// Thumbnail has to be a png image for backend

			const image = await FileUtils.readAsImage(file);

			return ImageUtils.image2RasterBase64String(this._transportLayer.appState.fonts, image, 256, "webp");
		}
		return "";
	}

	public async deleteDocument(document: DocumentModel, detailsPanelFeature: XyiconFeature) {
		let confirmed: boolean;
		const appState = this._transportLayer.appState;
		const modal = appState.currentUIVersion === "4.0" ? ConfirmWindow : ConfirmWindowV5;

		if (document.documentMap.length > 1) {
			confirmed = await modal.open("This document is attached to multiple objects. Are you sure you want to delete it?");
		} else {
			confirmed = await modal.open("Are you sure you want to delete this document?");
		}

		if (confirmed) {
			const feature = [XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog].includes(detailsPanelFeature)
				? XyiconFeature.OrganizationDocument
				: XyiconFeature.PortfolioDocument;

			await this._transportLayer.appState.actions.deleteItems([document], feature);
		}
	}
}
